(ns common.schema.entities.transaction-process
  (:require
   [clojure.string :as string]
   [common.country :as country]
   [common.schema.entities.common :as common]
   [common.schema.option-filters :as option-filters]
   [common.schema.schema-utils :as su]
   [common.util :as cu]))

(def status-options
  (->> [{:value :transaction-process.status/active
         :label "Active"}
        {:value :transaction-process.status/completed
         :label "Completed"}
        {:value :transaction-process.status/rejected
         :label "Rejected"}
        {:value :transaction-process.status/paused
         :label "Paused"}
        ;; :archived is legacy (pre-"completed"); kept so existing rows still
        ;; render a label. New completions use :completed; no code path sets
        ;; :archived anymore.
        {:value :transaction-process.status/archived
         :label "Archived"}]
       (mapv (fn [opt] (assoc opt :key (:value opt))))))

(def terminal-statuses
  #{:transaction-process.status/completed
    :transaction-process.status/rejected})

(defn terminal-status?
  [status]
  (contains? terminal-statuses status))

(def payment-term-options
  (->> [{:value :transaction-process.payment-term/quarterly
         :label "Quarterly"}
        {:value :transaction-process.payment-term/semi-annually
         :label "Semi-annually"}
        {:value :transaction-process.payment-term/annually
         :label "Annually"}]
       (mapv (fn [opt] (assoc opt :key (:value opt))))))

(def needed-value :transaction-process.needed/needed)
(def not-needed-value :transaction-process.needed/not-needed)

(def needed-options
  "Tri-state-friendly enum backing the per-decision 'is this decision needed?'
   fields. Kept keyword (not boolean) so customers can later add e.g. a
   conditionally-needed value without a data-type change. Canonical source — the
   engine and cards read these, never a hardcoded literal."
  [{:value needed-value :label "Required" :key needed-value}
   {:value not-needed-value :label "Not needed" :key not-needed-value}])

(def ^:private default-template-field-opts
  {:authorization            su/access-user-user
   :default-field-visibility :in-use})

(def ^:private default-template-calculated-field-opts
  {:default-field-visibility :in-use-disabled})

(def ^:private sub-entity-slot-opts
  "Opts shared by the named sub-entity slot refs. The slots are system-managed:
   not user-configurable in the field-config UI and not surfaced as ordinary
   fields."
  {:authorization              su/access-user-user
   :hidden-from-configuration? true
   :default-field-visibility   :not-in-use})

(def ^:private named-sub-entity-slot-fields
  "Storage model: every single sub-entity is its own cardinality-one ref on
   the transaction-process — the attribute IS the slot's identity, so Datomic's
   cardinality-one enforces 'one per slot'. Decisions
   keep their distinct entity types; attestations and declarations share one
   entity type each, so their per-slot config (outcome options, requiredness,
   sections) lives in the template field dictionary, not here. Labels are raw
   English; i18n is deferred."
  (let [decisions
        [[:transaction-process/pm-approval-decision "PM approval" :entity.type/pm-approval-decision]
         [:transaction-process/ic-decision "IC decision" :entity.type/ic-decision]
         [:transaction-process/advisory-board-decision "Advisory board decision" :entity.type/advisory-board-decision]
         [:transaction-process/aifm-decision "AIFM Board decision" :entity.type/aifm-decision]]
        attestations
        (mapv (fn [[attr label]] [attr label :entity.type/attestation])
              [[:transaction-process/coi-assessment "CoI assessment"]
               [:transaction-process/nominated-counsel-confirmation "Nominated counsel confirmation"]
               [:transaction-process/advisory-board-approval-needed "Advisory Board approval"]
               [:transaction-process/aifm-board-approval-needed "AIFM board approval"]
               [:transaction-process/investment-restrictions "Investment restrictions"]
               [:transaction-process/risk-mitigants-assessed "Risk & mitigants assessed"]
               [:transaction-process/dd-conducted "DD conducted"]
               [:transaction-process/capacity-calculation "Capacity calculation"]
               [:transaction-process/sustainability-considerations "Sustainability considerations"]
               [:transaction-process/kyc-check "KYC check"]
               [:transaction-process/luxco-date-approval "Lux date approval"]
               [:transaction-process/tax-considerations "Tax considerations"]])
        declarations
        (mapv (fn [[attr label]] [attr label :entity.type/declaration])
              [[:transaction-process/kyc-dd "KYC diligence complete"]
               [:transaction-process/legal-dd "Legal DD complete"]
               [:transaction-process/commercial-dd "Commercial DD complete"]
               [:transaction-process/technical-dd "Technical DD complete"]
               [:transaction-process/financial-dd "Financial DD complete"]
               [:transaction-process/insurance-dd "Insurance DD complete"]
               [:transaction-process/cyber-dd "Cyber DD complete"]
               [:transaction-process/esg-dd "ESG analysis complete"]
               [:transaction-process/tax-dd "Tax DD complete"]])]
    (mapcat (fn [[attr label entity-type]]
              (su/single-ref-field attr label [entity-type] sub-entity-slot-opts))
            (concat decisions attestations declarations))))

;; CapMan-specific read-only investment totals. They are CALCULATED fields
;; (registry-only — no Datomic attribute, no migration, system-write so the user
;; can never edit them). The `capman-` prefix scopes them to CapMan: the summing
;; rules are CapMan's ("Maximum = Sum of above" — the six equity-side components;
;; "Total = SUM of all" — Maximum + Debt amount), and the calc-fns must live in
;; this shared schema ns because the CapMan template namespaces depend on it (a
;; reverse dependency would be a cycle). Each amount carries its own currency
;; (`--sym`); summing amounts entered in different currencies is meaningless, so
;; when the contributing components disagree on currency the total resolves to nil.
(def ^:private capman-maximum-component-fields
  [:transaction-process/loan-principal-amount
   :transaction-process/equity-amount
   :transaction-process/transaction-costs
   :transaction-process/capital-transfer-tax
   :transaction-process/guarantee-amount
   :transaction-process/earnout-amount
   :transaction-process/currency-buffer])

(def ^:private capman-total-component-fields
  (conj capman-maximum-component-fields :transaction-process/debt-amount))

(defn- parse-number-string
  [s]
  (let [normalized (-> s string/trim (string/replace #"," ""))]
    (when-not (string/blank? normalized)
      #?(:clj  (try
                 (Double/parseDouble normalized)
                 (catch Exception _ nil))
         :cljs (let [n (js/Number normalized)]
                 (when-not (js/isNaN n)
                   n))))))

(defn- amount-component-number
  [->num value]
  (let [raw (if (string? value)
              (parse-number-string value)
              value)
        v (when (some? raw) (->num raw))]
    (cond
      (number? v) v
      (string? v) (parse-number-string v)
      :else nil)))

(defn- effective-component-fields
  [entity-data fields]
  (cond->> fields
           (false? (:transaction-process/debt-financing? entity-data))
           (remove #{:transaction-process/debt-amount})))

(defn- amount-components-currency
  "Single shared currency keyword across the contributing (non-nil-amount)
   components, `:currency/mismatch` when they resolve to two or more different
   currencies, or nil when none contributes. A component amount with no explicit
   `--sym` resolves to `default-currency` (the backend fills the default on save,
   and the form shows it before save), so a filled-but-unset field still counts
   as the default rather than being treated as a separate, summable currency."
  [entity-data fields default-currency]
  (let [fields (effective-component-fields entity-data fields)
        syms (->> fields
                  (filter #(some? (amount-component-number identity (get entity-data %))))
                  (keep #(or (get entity-data (cu/currency->symbol %)) default-currency))
                  distinct)]
    (case (count syms)
      0 nil
      1 (first syms)
      :currency/mismatch)))

(defn- sum-amount-components
  "Sum of the components' amounts, or nil when they mix currencies or are all
   empty. `bigdec->float` (supplied by the calc resolver — on both backend and
   frontend) normalises the amounts to plain numbers before summing. The
   `default-currency` (frontend only) lets a filled amount whose `--sym` is still
   unset count toward the sum."
  [{:keys [entity-data bigdec->float default-currency]} fields]
  (let [fields (effective-component-fields entity-data fields)]
    (when (not= :currency/mismatch (amount-components-currency entity-data fields default-currency))
      (let [->num (or bigdec->float identity)
            vals (keep #(amount-component-number ->num (get entity-data %)) fields)]
        (when (seq vals)
          (reduce + vals))))))

(defn- amount-components-shared-currency
  "The shared currency for the computed total's `--sym`, or nil when none/mixed."
  [{:keys [entity-data default-currency]} fields]
  (let [fields (effective-component-fields entity-data fields)
        cur (amount-components-currency entity-data fields default-currency)]
    (when (and (some? cur) (not= :currency/mismatch cur))
      cur)))

(def ^:private default-investment-size-component-fields
  [:transaction-process/equity-amount
   :transaction-process/debt-amount
   :transaction-process/transaction-costs
   :transaction-process/taxes-amount
   :transaction-process/guarantee-amount
   :transaction-process/earnout-amount])

(def ^:private attachment-slot-field-specs
  [{:attr  :transaction-process-attachment/investment-memo
    :label "Investment memo"}
   {:attr  :transaction-process-attachment/diligence-reports
    :label "Diligence report(s)"}
   {:attr  :transaction-process-attachment/signing-documents
    :label "Signing documents"}
   {:attr  :transaction-process-attachment/closing-documents
    :label "Closing documents"}
   {:attr  :transaction-process-attachment/structure-chart
    :label "Structure chart"}
   {:attr  :transaction-process-attachment/icm
    :label "ICM"}
   {:attr  :transaction-process-attachment/sfdr-conformity-checklist
    :label "Sustainability Considerations (SFDR conformity checklist)"}
   {:attr  :transaction-process-attachment/other-documents
    :label "Other documents"}
   {:attr  :transaction-process-attachment/poa
    :label "PoA"}
   {:attr  :transaction-process-attachment/spa
    :label "SPA"}
   {:attr  :transaction-process-attachment/sha
    :label "SHA"}
   {:attr  :transaction-process-attachment/funds-flow
    :label "Funds Flow"}
   {:attr  :transaction-process-attachment/loan-documents
    :label "Loan documents"}
   {:attr  :transaction-process-attachment/signing-other-documents
    :label "Other documents"}
   {:attr  :transaction-process-attachment/closing-memo
    :label "Closing memo"}
   {:attr  :transaction-process-attachment/funds-flow-documents
    :label "Funds flow documents"}
   {:attr  :transaction-process-attachment/closing-other-documents
    :label "Other documents"}])

(defn- attachment-slot-value
  [slot-kw entity-data]
  (->> (:common/attachments entity-data)
       (filterv (fn [attachment]
                  (some #{slot-kw} (:common/system-kws attachment))))))

(defn- attachment-slot-calculated-field
  [{:keys [attr label]}]
  (su/calculated-field attr
                       label
                       {:data-type                :data.type/ref-multi
                        :entity-types             [:entity.type/attachment]
                        :default-field-visibility (:default-field-visibility
                                                    default-template-calculated-field-opts)
                        :composite-attrs          [:common/attachments :common/system-kws]
                        :calculation-triggers     {:entity.type/attachment [[:common/_attachments]]}
                        :calc-fn                  (fn [{:keys [entity-data]}]
                                                    (attachment-slot-value attr entity-data))}))

(def schema
  (vec
    (concat

      ;; User-facing fields

      (su/text-simple-field :transaction-process/name
                            "Transaction name"
                            {:authorization            su/access-user-user
                             :default-field-visibility :in-use})

      (su/text-simple-field :transaction-process/short-name
                            "Transaction short name"
                            {:authorization            su/access-user-user
                             :default-field-visibility :in-use
                             ;; Marks this as THE canonical name field for
                             ;; :entity.type/transaction-process -
                             ;; ui.view.grid-opensearch.sub/ag-grid-col-defs
                             ;; picks its "selected name column" (always
                             ;; visible, pinned left) by looking for a field
                             ;; whose :field/attribute equals
                             ;; :field/name-attribute; every other ported
                             ;; entity's own name field sets this the same
                             ;; way (see sale-agreement/name,
                             ;; investor/name). Without it, that lookup finds
                             ;; no match and falls back to
                             ;; (first columns) - the schema's internal
                             ;; field map has 150+ entries so it's a
                             ;; PersistentHashMap, not an ordered map, so
                             ;; "first" is really an arbitrary hash-ordered
                             ;; field (observed: :transaction-process/insurance-dd,
                             ;; a hidden-from-configuration? sub-entity slot
                             ;; ref) pinned left ahead of the real name
                             ;; column (see ai/mistakes.md).
                             :name-attribute           :transaction-process/short-name})

      (su/single-ref-field :transaction-process/vehicle
                           "Vehicle"
                           [:entity.type/fund]
                           {:authorization            su/access-user-user
                            :default-field-visibility :in-use})

    ;; Generic source entity for starting and discovering a transaction process.
    ;; Opportunities are the first supported target; the allowed entity-type list
    ;; can grow without changing the attribute or creation contract.
    (su/single-ref-field :transaction-process/target
                         "Target"
                         [:entity.type/oppo]
                         {:authorization              su/access-user-user
                          :hidden-from-configuration? true
                          :default-field-visibility   :not-in-use})

    ;; Snapshotted from the vehicle fund's :fund/lux-fund? at create time
    ;; (backend.domain.transaction-process/create) and never propagated back if the
    ;; fund later flips — a deal's Lux-ness is fixed when it starts, so already-passed
    ;; stages can't sprout a newly-required field. Drives the Lux-only field/slot
    ;; visibility rules; system-managed, not a user-entered form field.
    (su/boolean-field :transaction-process/lux-fund?
                      "Lux fund"
                      {:authorization              su/access-user-user
                       :default-field-visibility   :not-in-use-blocked})

      (su/keyword-multi-field :transaction-process/investment-type
                              "Investment type"
                              []                            ;; options come from template
                              {:authorization            su/access-user-user
                               :default-field-visibility :in-use})

      (su/keyword-field :transaction-process/transaction-type
                        "Transaction type"
                        []                                  ;; options come from template
                        {:authorization            su/access-user-user
                         :default-field-visibility :in-use})

      (su/keyword-field :transaction-process/stage
                        "Stage"
                        []                                  ;; options come from template
                        {:authorization            su/access-user-user
                         :default-field-visibility :in-use})

      (su/keyword-field :transaction-process/status
                        "Status"
                        status-options
                        {:authorization            su/access-user-user
                         :default-field-visibility :in-use})

      ;; Core proposal fields

      (su/currency-field :transaction-process/dd-budget
                         "DD budget"
                         default-template-field-opts)

      (su/text-simple-field :transaction-process/transaction-note
                            "Transaction note"
                            default-template-field-opts)

      (su/text-simple-field :transaction-process/target-entities
                            "Target entity / entities"
                            default-template-field-opts)

    (su/single-ref-field :transaction-process/deal-captain
                         "Deal captain"
                         [:entity.type/account]
                         (merge default-template-field-opts
                                {:option-filter-fn option-filters/active-users
                                 :os-filter-terms-fn
                                 (fn [_args]
                                   {"extras.active-user" true
                                    "extras.account-modules.keyword"
                                    (subs (str :module/transaction-process) 1)})}))

      (su/text-simple-field :transaction-process/target-entity-business-ids
                            "Target entity business id(s)"
                            default-template-field-opts)

      (su/text-simple-field :transaction-process/target-asset-property-ids
                            "Target asset property id(s)"
                            default-template-field-opts)

      (su/text-simple-field :transaction-process/counterparties
                            "Counterparty / counterparties"
                            default-template-field-opts)

      (su/text-simple-field :transaction-process/target-company-location
                            "Target company location"
                            default-template-field-opts)

      (su/text-simple-field :transaction-process/property-location
                            "Property location"
                            default-template-field-opts)

      ;; CapMan feedback: drop "HQ" from the labels (keywords keep -hq- so no
      ;; migration). NB the related request to remove country/city from the RE
      ;; template is a field-drop and is deferred (see triage doc).
      (su/keyword-field :transaction-process/target-company-hq-country
                        "Target company country"
                        country/options
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/target-company-hq-city
                            "Target company city"
                            default-template-field-opts)

      (su/keyword-field :transaction-process/property-country
                        "Property country"
                        country/options
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/property-city
                            "Property city"
                            default-template-field-opts)

      (su/percentage-field :transaction-process/counterparty-ownership
                           "Counterparty ownership"
                           default-template-field-opts)

      ;; The stake the fund acquires. Relabelled "Planned ownership percentage" and
      ;; made mandatory in the CapMan template (2026-06); the legacy Counterparty
      ;; ownership field above is no longer surfaced.
      (su/percentage-field :transaction-process/acquired-ownership
                           "Acquired ownership"
                           default-template-field-opts)

    ;; Competition-law fields. The strategic-veto-rights attribute remains in the
    ;; shared schema for compatibility even though templates do not surface it.
    (su/boolean-field :transaction-process/competition-law-control?
                      "Will the transaction result in the fund acquiring sole control or joint control over the target company under competition law?"
                      default-template-field-opts)

      (su/percentage-field :transaction-process/competition-law-control-percentage
                           "Control according to the competition law"
                           default-template-field-opts)

      (su/text-simple-field :transaction-process/competition-law-veto-rights
                            "Strategic veto rights"
                            default-template-field-opts)

      ;; Co-investors: Yes/No, with the names + ownership percentages collected when Yes.
      (su/boolean-field :transaction-process/co-investors?
                        "Does the transaction include co-investors?"
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/co-investors-details
                            "Name and ownership percentages of the co-investor(s)"
                            default-template-field-opts)

      ;; MIP transactions: diluted ownership before/after, shown only for MIP deals.
      (su/percentage-field :transaction-process/diluted-ownership-current
                           "Diluted ownership, current (%)"
                           default-template-field-opts)

      (su/percentage-field :transaction-process/diluted-ownership-after
                           "Diluted ownership (%) after"
                           default-template-field-opts)

      (su/percentage-field :transaction-process/base-case-irr
                           "Base case IRR"
                           default-template-field-opts)

    (su/multiplier-field :transaction-process/base-case-tmm
                         "Base case TMM"
                         default-template-field-opts)

      (su/currency-field :transaction-process/transaction-purchase-price
                         "Transaction purchase price"
                         default-template-field-opts)

      (su/numeric-field :transaction-process/fx-rate
                        "FX rate"
                        default-template-field-opts)

      (su/currency-field :transaction-process/transaction-costs
                         "Transaction costs"
                         default-template-field-opts)

      (su/currency-field :transaction-process/capital-transfer-tax
                         "Capital transfer tax"
                         default-template-field-opts)

      (su/currency-field :transaction-process/taxes-amount
                         "Taxes"
                         default-template-field-opts)

      ;; Debt / Nest strategy fields

      (su/currency-field :transaction-process/pik-interest
                         "PIK interest"
                         default-template-field-opts)

      (su/currency-field :transaction-process/cash-interest
                         "Cash interest"
                         default-template-field-opts)

      (su/date-field :transaction-process/maturity
                     "Maturity"
                     default-template-field-opts)

      (su/keyword-field :transaction-process/payment-term
                        "Payment term"
                        payment-term-options
                        default-template-field-opts)

      (su/boolean-field :transaction-process/equity-participation?
                        "Equity participation"
                        default-template-field-opts)

      (su/keyword-field :transaction-process/equity-participation-type
                        "Equity participation type"
                        []
                        default-template-field-opts)

      (su/currency-field :transaction-process/waiver-fee
                         "Waiver fee"
                         default-template-field-opts)

      ;; Real Estate strategy fields

      (su/numeric-field :transaction-process/distance-from-closest-major-city-km
                        "Distance from closest major city (km)"
                        default-template-field-opts)

      (su/percentage-field :transaction-process/passing-rent-on-necessity-based-retail
                           "Passing rent on necessity based retail"
                           default-template-field-opts)

      (su/percentage-field :transaction-process/lettable-area-in-retail-use
                           "Lettable area in retail use"
                           default-template-field-opts)

      (su/percentage-field :transaction-process/pre-lease-sold-percentage
                           "Pre-lease / Sold percentage"
                           default-template-field-opts)

      ;; Investment amount breakdown

      (su/currency-field :transaction-process/maximum-investment-amount
                         "Maximum investment amount"
                         default-template-field-opts)

      (su/currency-field :transaction-process/equity-amount
                         "Equity"
                         default-template-field-opts)

      ;; Loan / Principal amount for debt-style (Nest) deals — the fund's invested
      ;; principal, distinct from Equity. A CapMan maximum-component (see
      ;; capman-maximum-component-fields), shown only in the Nest template layout; it
      ;; stays nil (non-contributing) for templates that don't surface it.
      (su/currency-field :transaction-process/loan-principal-amount
                         "Loan / Principal"
                         default-template-field-opts)

      (su/currency-field :transaction-process/guarantee-amount
                         "Guarantee"
                         default-template-field-opts)

      (su/text-simple-field :transaction-process/guarantee-details
                            "Guarantee details"
                            default-template-field-opts)

      (su/currency-field :transaction-process/earnout-amount
                         "Earnout"
                         default-template-field-opts)

      (su/currency-field :transaction-process/currency-buffer
                         "Currency buffer"
                         default-template-field-opts)

      ;; Debt financing (transaction-level, separate from Nest debt instrument fields)

      (su/boolean-field :transaction-process/debt-financing?
                        "Debt financing"
                        default-template-field-opts)

      (su/keyword-field :transaction-process/debt-financing-type
                        "Debt type"
                        [{:key :transaction-process.debt-type/acquisition-loan :label "Acquisition loan"}
                         {:key :transaction-process.debt-type/vendor-loan :label "Vendor loan"}
                         {:key :transaction-process.debt-type/capex-loan :label "Capex loan"}]
                        default-template-field-opts)

      (su/currency-field :transaction-process/debt-amount
                         "Debt amount"
                         default-template-field-opts)

      (su/boolean-field :transaction-process/debt-financing-secured?
                        "Is the debt financing secured (Credit Committee approval)"
                        default-template-field-opts)

      ;; CapMan read-only calculated totals (see capman-*-component-fields above).
      ;; Calculated fields: registry-only, no migration, system-write. Their
      ;; currency-symbol companions are calculated too, so the form renders the
      ;; shared currency (and nothing when the components mix currencies).
      (su/calculated-field :transaction-process/capman-maximum-investment-amount
                           "Maximum investment amount"
                           {:data-type                 :data.type/currency-value
                            :composite-attrs           capman-maximum-component-fields
                            :entity-types              [:entity.type/transaction-process]
                            :default-field-visibility  :not-in-use-blocked
                            :field/secondary-attribute :transaction-process/capman-maximum-investment-amount--sym
                            :calc-fn                   (fn [args]
                                                         (sum-amount-components args capman-maximum-component-fields))})

      (su/calculated-field :transaction-process/capman-maximum-investment-amount--sym
                           "Maximum investment amount (currency)"
                           {:data-type                :data.type/currency-symbol
                            :value-type               :keyword
                            :composite-attrs          capman-maximum-component-fields
                            :entity-types             [:entity.type/transaction-process]
                            :default-field-visibility :not-in-use-blocked
                            :calc-fn                  (fn [args]
                                                        (amount-components-shared-currency
                                                          args capman-maximum-component-fields))})

      (su/calculated-field :transaction-process/capman-total-investment-amount
                           "Total investment amount"
                           {:data-type                 :data.type/currency-value
                            :composite-attrs           capman-total-component-fields
                            :entity-types              [:entity.type/transaction-process]
                            :default-field-visibility  :not-in-use-blocked
                            :field/secondary-attribute :transaction-process/capman-total-investment-amount--sym
                            :calc-fn                   (fn [args]
                                                         (sum-amount-components args capman-total-component-fields))})

      (su/calculated-field :transaction-process/capman-total-investment-amount--sym
                           "Total investment amount (currency)"
                           {:data-type                :data.type/currency-symbol
                            :value-type               :keyword
                            :composite-attrs          capman-total-component-fields
                            :entity-types             [:entity.type/transaction-process]
                            :default-field-visibility :not-in-use-blocked
                            :calc-fn                  (fn [args]
                                                        (amount-components-shared-currency
                                                          args capman-total-component-fields))})

      (su/calculated-field :transaction-process/investment-size-total
                           "Investment size (total)"
                           {:data-type                 :data.type/currency-value
                            :composite-attrs           default-investment-size-component-fields
                            :entity-types              [:entity.type/transaction-process]
                            :default-field-visibility  (:default-field-visibility
                                                         default-template-calculated-field-opts)
                            :field/secondary-attribute :transaction-process/investment-size-total--sym
                            :calc-fn                   (fn [args]
                                                         (sum-amount-components
                                                           args
                                                           default-investment-size-component-fields))})

      (su/calculated-field :transaction-process/investment-size-total--sym
                           "Investment size (total currency)"
                           {:data-type                :data.type/currency-symbol
                            :value-type               :keyword
                            :composite-attrs          default-investment-size-component-fields
                            :entity-types             [:entity.type/transaction-process]
                            :default-field-visibility (:default-field-visibility
                                                        default-template-calculated-field-opts)
                            :calc-fn                  (fn [args]
                                                        (amount-components-shared-currency
                                                          args
                                                          default-investment-size-component-fields))})

      ;; Document slots are calculated fields over the generic attachment bag.
      ;; Template dictionaries still mark them as :data.type/attachment-slot so
      ;; the transaction-process form renders upload controls instead of ordinary
      ;; read-only ref-multi fields.
      (mapcat attachment-slot-calculated-field attachment-slot-field-specs)

      ;; DD types conducted by the investment team
      (su/keyword-multi-field :transaction-process/dd-types-conducted
                              "DD types conducted"
                              []                            ;; options come from template
                              default-template-field-opts)

      (su/percentage-field :transaction-process/dividend-yield
                           "Dividend yield (base case)"
                           default-template-field-opts)

      (su/text-multiline-field :transaction-process/proposal-text
                               "Proposal text"
                               default-template-field-opts)

      ;; Conflict of interest fields

      (su/keyword-field :transaction-process/coi-other-capman-entity
                        "Is there any other CapMan entity involved in the transaction?"
                        []                                  ;; options come from template
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-other-capman-entity-comment
                            "Is there any other CapMan entity involved in the transaction - comment"
                            default-template-field-opts)

      (su/keyword-field :transaction-process/coi-capman-person-involved
                        "Is there CapMan person involved in the transaction?"
                        []
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-capman-person-involved-comment
                            "Is there CapMan person involved in the transaction - comment"
                            default-template-field-opts)

      (su/keyword-field :transaction-process/coi-counterparty-linked-to-fund
                        "Is the counterparty in any way linked to the fund in question"
                        []
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-counterparty-linked-to-fund-comment
                            "Is the counterparty any way linked to the fund in question - comment"
                            default-template-field-opts)

      (su/keyword-field :transaction-process/coi-tax-material-risk
                        "Is there a material risk for challenge by any tax authority on any significant tax positions of the transaction or the structure?"
                        []
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-tax-material-risk-comment
                            "Is there a material risk for challenge by any tax authority on any significant tax positions of the transaction or the structure? - comment"
                            default-template-field-opts)

      (su/keyword-field :transaction-process/coi-tax-avoidance-artificial-steps
                        "Does the transaction involve artificial steps or arrangement that do not reflect economic reality or are otherwise implemented for tax avoidance purposes?"
                        []
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-tax-avoidance-artificial-steps-comment
                            "Does the transaction involve artificial steps or arrangement that do not reflect economic reality or are otherwise implemented for tax avoidance purposes? - comment"
                            default-template-field-opts)

      ;; CapMan 2026-06: new CoI policy question (own CoI subsection in the template).
      ;; The label names CapMan's CoI policy, so the ident carries -capman- and the
      ;; field stays :not-in-use-blocked (default-template-field-opts) — same convention as the
      ;; coi-*-capman-* questions above.
      (su/keyword-field :transaction-process/coi-capman-conflicts-handled
                        "Have any actual or potential conflicts of interest been identified, and if so, have they been assessed and handled in accordance with CapMan's Conflict of Interest policy?"
                        []                                  ;; options come from template
                        default-template-field-opts)

      (su/text-simple-field :transaction-process/coi-capman-conflicts-handled-comment
                            "Have any actual or potential conflicts of interest been identified, and if so, have they been assessed and handled in accordance with CapMan's Conflict of Interest policy? - comment"
                            default-template-field-opts)

      ;; CapMan 2026-06: Tax considerations subsection extras. The two tax questions
      ;; above (coi-tax-*) move into a dedicated Tax considerations card in the
      ;; template; these add the cross-border + DAC6 follow-ups.
      (su/boolean-field :transaction-process/tax-cross-border?
                        "Is this a cross border arrangement?"
                        default-template-field-opts)

      (su/boolean-field :transaction-process/tax-dac6-non-reportable?
                        "Has an external tax advisor confirmed that the arrangement is non-reportable under DAC6?"
                        default-template-field-opts)

    ;; CapMan 2026-06: Nominated Counsel Confirmations subsection. Company + person
    ;; are CRM references resolved through the entity-search picker; each template
    ;; controls whether the picker also offers entity creation.
    (su/single-ref-field :transaction-process/nominated-counsel-company
                         "Nominated counsel company"
                         [:entity.type/company]
                         (merge default-template-field-opts
                                {:select-entity-types [:entity.type/company]
                                 :editor-type         :editor.type/search}))

      (su/single-ref-field :transaction-process/nominated-counsel-person
                           "Nominated counsel person"
                           [:entity.type/person]
                           (merge default-template-field-opts
                                  {:select-entity-types [:entity.type/person]
                                   :editor-type         :editor.type/search}))

      (su/boolean-field :transaction-process/nominated-counsel-reviewed?
                        "Has nominated counsel reviewed and analysed the relevant regulatory, competition and contractual matters?"
                        default-template-field-opts)

      (su/keyword-field :transaction-process/gp-affiliates-involved
                        "Are there GP affiliates involved in the transaction?"
                        []                                  ;; options come from template
                        default-template-field-opts)

      (su/keyword-field :transaction-process/lp-affiliates-involved
                        "Are there LP affiliates involved in the transaction?"
                        []                                  ;; options come from template
                        default-template-field-opts)

      (su/keyword-field :transaction-process/tax-avoidance-artificial-steps
                        "Does the transaction involve artificial steps or arrangements that do not reflect economic reality or are otherwise implemented for tax avoidance purposes?"
                        []                                  ;; options come from template
                        default-template-field-opts)

      ;; Anticipated dates

      (su/date-field :transaction-process/anticipated-decision-date
                     "Anticipated decision date"
                     default-template-field-opts)

      (su/date-field :transaction-process/anticipated-ic-decision-date
                     "Anticipated IC decision date"
                     default-template-field-opts)

      (su/date-field :transaction-process/anticipated-luxco-decision-date
                     "Anticipated LuxCo decision date"
                     default-template-field-opts)

      (su/date-field :transaction-process/anticipated-signing-date
                     "Anticipated signing date"
                     default-template-field-opts)

      (su/date-field :transaction-process/anticipated-closing-date
                     "Anticipated closing date"
                     default-template-field-opts)

      (su/date-field :transaction-process/final-closing-date
                     "Final closing date"
                     default-template-field-opts)

      ;; Per-decision "is this decision needed?" fields. Set during Reviewing,
      ;; consumed by the Approving stage to decide which decision slots are
      ;; required. Requiredness is a first-class, audited, permissioned field resolved
      ;; via the normal rule machinery. The dictionary's decision slot points at its
      ;; backing field via :slot/required {:from-field ...}.

      (su/keyword-field :transaction-process/pm-approval-decision-needed
                        "PM approval needed"
                        needed-options
                        default-template-field-opts)

      (su/keyword-field :transaction-process/ic-decision-needed
                        "IC decision needed"
                        needed-options
                        default-template-field-opts)

      (su/keyword-field :transaction-process/advisory-board-decision-needed
                        "Advisory board decision needed"
                        needed-options
                        default-template-field-opts)

      (su/keyword-field :transaction-process/aifm-decision-needed
                        "AIFM board decision needed"
                        needed-options
                        default-template-field-opts)

      ;; Closing-stage payment confirmation (Fund Manager fills on Closing):
      ;; a yes/no that the closing amount has been paid, plus the date it was paid.

      (su/boolean-field :transaction-process/closing-amount-paid?
                        "Closing amount paid"
                        default-template-field-opts)

      (su/date-field :transaction-process/closing-amount-paid-date
                     "Closing amount paid on"
                     default-template-field-opts)

      ;; Closing-stage headline fields (CapMan testing 2026-05-25): the actual
      ;; closing date and final closing amount of the deal, distinct from the
      ;; amount actually paid above.

      (su/currency-field :transaction-process/closing-amount
                         "Closing amount"
                         default-template-field-opts)

      (su/date-field :transaction-process/actual-closing-date
                     "Actual closing date"
                     default-template-field-opts)

      ;; Nest-strategy: text-form interest rate (e.g. "4 + EURIBOR 3m") and free-text
      ;; equity participation notes.

      (su/percentage-field :transaction-process/pik-interest-rate
                           "PIK interest"
                           default-template-field-opts)

      (su/text-simple-field :transaction-process/cash-interest-rate-text
                            "Cash interest"
                            default-template-field-opts)

      (su/text-multiline-field :transaction-process/equity-participation-notes
                               "Equity participation notes"
                               default-template-field-opts)

      ;; Set once on creation, not configurable in UI
      (su/keyword-field :transaction-process/template-kw
                        "Template"
                        []                                  ;; options come from vehicle + investment type config
                        {:authorization            su/access-user-user
                         :hidden-from-config?      true
                         :default-field-visibility :not-in-use})

      ;; Sub-entity refs: each single sub-entity is a named cardinality-one
      ;; ref. See `named-sub-entity-slot-fields` above.
      named-sub-entity-slot-fields

      (su/text-edn-field :transaction-process/team-assignments-edn
                         "Team role assignments"
                         {:authorization            su/access-user-user
                          :hidden-from-config?      true
                          :default-field-visibility :not-in-use})

      ;; Attachments
      common/attachment-schema)))
